import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  User, ChevronRight, ChevronLeft, Sparkles, PenLine,
  Upload, FileText, Loader2, AlertCircle, X, CheckCircle,
  Building2, Award, Tag, Plus, Trash2, Eye, RotateCcw,
  ChevronDown, ChevronUp
} from "lucide-react";
import { specializations as allSpecializations } from "../../data/experts";
import { supabase } from "../../lib/supabase";
import { invokeFunction } from "../../lib/invokeFunction";
import { extractTextFromPdf, isPdfFile } from "../../lib/pdfExtractor";
import StepPhoto from "./OnboardingStepPhoto";

const MAX_TOPICS = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain"
];

function getFileTypeLabel(type) {
  if (type === "application/pdf") return "PDF";
  if (type.includes("word")) return "Word";
  if (type === "text/plain") return "TXT";
  return "File";
}

export default function ExpertOnboardingWizard({ profile, setProfile, onComplete, onSaveProfile, userId, avatarUrl, onAvatarChange }) {
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState(() => {
    const parts = (profile.name || "").trim().split(" ");
    return parts[0] || "";
  });
  const [lastName, setLastName] = useState(() => {
    const parts = (profile.name || "").trim().split(" ");
    return parts.slice(1).join(" ") || "";
  });

  const [fillMode, setFillMode] = useState(null);
  const [aiStep, setAiStep] = useState("upload");
  const [aiPreview, setAiPreview] = useState(false);
  const [file, setFile] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [selectedAiFields, setSelectedAiFields] = useState({
    title: true, bio: true, companies: true, achievements: true, specializations: true
  });
  const [expandedAi, setExpandedAi] = useState({
    title: true, bio: true, companies: true, achievements: true, specializations: true
  });
  const fileInputRef = React.useRef(null);

  const [manualBio, setManualBio] = useState(profile.bio || "");
  const [manualTitle, setManualTitle] = useState(profile.title || "");
  const [manualCompanies, setManualCompanies] = useState(
    Array.isArray(profile.companies) && profile.companies.length > 0
      ? profile.companies
      : [{ name: "", position: "", years: "" }]
  );
  const [manualAchievements, setManualAchievements] = useState(
    Array.isArray(profile.achievements) && profile.achievements.length > 0
      ? profile.achievements
      : [""]
  );

  const [selectedTopics, setSelectedTopics] = useState(
    Array.isArray(profile.specializations) ? profile.specializations.slice(0, MAX_TOPICS) : []
  );

  const totalSteps = 4;

  const handleNextFromName = () => {
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    if (!fullName) return;
    setProfile((prev) => ({ ...prev, name: fullName }));
    setStep(2);
  };

  const handleChooseAi = () => {
    setFillMode("ai");
  };

  const handleChooseManual = () => {
    setFillMode("manual");
  };

  const handleSwitchToAi = () => {
    setFillMode("ai");
    setAiStep("upload");
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!ALLOWED_TYPES.includes(selected.type)) {
      setAiError("Поддерживаются форматы: PDF, Word (.doc, .docx), TXT");
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setAiError("Максимальный размер файла: 5 МБ");
      return;
    }
    setAiError("");
    setFile(selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFileSelect({ target: { files: [dropped] } });
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setAiLoading(true);
    setAiError("");
    try {
      let fileContent;
      let isBase64 = false;

      if (isPdfFile(file)) {
        console.log("[AI CV] Extracting PDF text on client with pdfjs-dist...");
        const pdfText = await extractTextFromPdf(file);
        console.log(`[AI CV] PDF extracted: ${pdfText.length} chars`);
        if (pdfText.length < 50) {
          throw new Error("Не удалось извлечь текст из PDF. Попробуйте сохранить резюме в формате TXT и загрузить снова.");
        }
        fileContent = pdfText;
        isBase64 = false;
      } else if (file.type === "text/plain") {
        fileContent = await file.text();
      } else {
        fileContent = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.readAsDataURL(file);
        });
        isBase64 = true;
      }

      const { data, error: fnError } = await invokeFunction("deepseek-parse-cv", {
        body: { fileContent, fileType: file.type, fileName: file.name, isBase64 }
      });
      if (fnError) throw new Error(fnError.message || "Ошибка анализа резюме");
      if (data?.profile) {
        console.log("[AI CV] Parsed profile:", JSON.stringify(data.profile, null, 2));
        const p = data.profile;
        const hasContent = (p.title && p.title.trim()) || (p.bio && p.bio.trim()) ||
          (Array.isArray(p.companies) && p.companies.length > 0) ||
          (Array.isArray(p.achievements) && p.achievements.length > 0) ||
          (Array.isArray(p.specializations) && p.specializations.length > 0);
        if (!hasContent && p.name && p.name.trim()) {
          setAiResult(p);
          setAiStep("review");
        } else if (hasContent) {
          setAiResult(p);
          setAiStep("review");
        } else {
          throw new Error("AI-помощник извлёк данные, но все поля оказались пустыми. Попробуйте загрузить файл в формате TXT.");
        }
      } else {
        throw new Error(data?.error || "AI-помощник не смог извлечь данные из файла");
      }
    } catch (err) {
      setAiError(err.message || "Ошибка при анализе резюме");
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplyAi = () => {
    if (!aiResult) return;
    const updates = {};
    if (selectedAiFields.title && aiResult.title) updates.title = aiResult.title;
    if (selectedAiFields.bio && aiResult.bio) updates.bio = aiResult.bio;
    if (selectedAiFields.companies && aiResult.companies?.length > 0) {
      updates.companies = aiResult.companies;
    }
    if (selectedAiFields.achievements && aiResult.achievements?.length > 0) {
      updates.achievements = aiResult.achievements;
    }
    if (selectedAiFields.specializations && aiResult.specializations?.length > 0) {
      const merged = [...new Set(aiResult.specializations)].slice(0, MAX_TOPICS);
      updates.specializations = merged;
      setSelectedTopics(merged);
    }
    setProfile((prev) => ({ ...prev, ...updates }));
    setAiPreview(false);
    setStep(3);
  };

  const handleApplyManual = () => {
    const cleanCompanies = manualCompanies.filter((c) => c.name.trim());
    const cleanAchievements = manualAchievements.filter((a) => a.trim());
    setProfile((prev) => ({
      ...prev,
      title: manualTitle,
      bio: manualBio,
      companies: cleanCompanies,
      achievements: cleanAchievements
    }));
    setStep(3);
  };

  const handleFinish = async () => {
    setProfile((prev) => ({ ...prev, specializations: selectedTopics }));
    if (onSaveProfile) {
      await onSaveProfile({
        ...profile,
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        specializations: selectedTopics
      });
    }
    if (onComplete) onComplete();
  };

  const toggleTopic = (topic) => {
    setSelectedTopics((prev) => {
      if (prev.includes(topic)) return prev.filter((t) => t !== topic);
      if (prev.length >= MAX_TOPICS) return prev;
      return [...prev, topic];
    });
  };

  const countAiFields = () => {
    let total = 0;
    let selected = 0;
    const r = aiResult || {};
    if (r.title && typeof r.title === "string" && r.title.trim()) { total++; if (selectedAiFields.title) selected++; }
    if (r.bio && typeof r.bio === "string" && r.bio.trim()) { total++; if (selectedAiFields.bio) selected++; }
    if (Array.isArray(r.companies) && r.companies.length > 0) { total++; if (selectedAiFields.companies) selected++; }
    if (Array.isArray(r.achievements) && r.achievements.length > 0) { total++; if (selectedAiFields.achievements) selected++; }
    if (Array.isArray(r.specializations) && r.specializations.length > 0) { total++; if (selectedAiFields.specializations) selected++; }
    return { total, selected };
  };

  return (
    <div id="expert-onboarding-wizard_root" className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div key={i} className="flex items-center gap-3 flex-1">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-medium transition-colors ${
              step > i + 1 ? "bg-accent-600 text-white" : step === i + 1 ? "bg-primary-600 text-white" : "bg-neutral-800 text-neutral-500"
            }`}>
              {step > i + 1 ? <CheckCircle className="w-5 h-5" /> : i + 1}
            </div>
            {i < totalSteps - 1 && (
              <div className={`flex-1 h-0.5 rounded-full transition-colors ${step > i + 1 ? "bg-accent-600" : "bg-neutral-800"}`} />
            )}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <StepName
            key="step-name"
            firstName={firstName}
            lastName={lastName}
            setFirstName={setFirstName}
            setLastName={setLastName}
            onNext={handleNextFromName}
          />
        )}

        {step === 2 && !fillMode && (
          <StepChooseMethod
            key="step-choose"
            onBack={() => setStep(1)}
            onChooseAi={handleChooseAi}
            onChooseManual={handleChooseManual}
          />
        )}

        {step === 2 && fillMode === "ai" && (
          aiPreview ? (
            <StepManualFill
              key="step-ai-preview"
              manualTitle={manualTitle}
              setManualTitle={setManualTitle}
              manualBio={manualBio}
              setManualBio={setManualBio}
              manualCompanies={manualCompanies}
              setManualCompanies={setManualCompanies}
              manualAchievements={manualAchievements}
              setManualAchievements={setManualAchievements}
              onBack={() => setAiPreview(false)}
              onSwitchToAi={null}
              onNext={handleApplyAi}
              isAiPreview
            />
          ) : (
            <StepAiFill
              key="step-ai"
              aiStep={aiStep}
              file={file}
              setFile={setFile}
              fileInputRef={fileInputRef}
              aiLoading={aiLoading}
              aiError={aiError}
              setAiError={setAiError}
              aiResult={aiResult}
              selectedAiFields={selectedAiFields}
              setSelectedAiFields={setSelectedAiFields}
              expandedAi={expandedAi}
              setExpandedAi={setExpandedAi}
              onBack={() => setFillMode(null)}
              handleFileSelect={handleFileSelect}
              handleDrop={handleDrop}
              handleAnalyze={handleAnalyze}
              handleApplyAi={handleApplyAi}
              handleReset={() => { setFile(null); setAiResult(null); setAiError(""); setAiStep("upload"); setAiPreview(false); }}
              countAiFields={countAiFields}
              profile={profile}
              onPreview={() => {
                if (!aiResult) return;
                if (aiResult.title) setManualTitle(aiResult.title);
                if (aiResult.bio) setManualBio(aiResult.bio);
                if (Array.isArray(aiResult.companies) && aiResult.companies.length > 0) {
                  setManualCompanies(aiResult.companies);
                }
                if (Array.isArray(aiResult.achievements) && aiResult.achievements.length > 0) {
                  setManualAchievements(aiResult.achievements);
                }
                setAiPreview(true);
              }}
            />
          )
        )}

        {step === 2 && fillMode === "manual" && (
          <StepManualFill
            key="step-manual"
            manualTitle={manualTitle}
            setManualTitle={setManualTitle}
            manualBio={manualBio}
            setManualBio={setManualBio}
            manualCompanies={manualCompanies}
            setManualCompanies={setManualCompanies}
            manualAchievements={manualAchievements}
            setManualAchievements={setManualAchievements}
            onBack={() => setFillMode(null)}
            onSwitchToAi={handleSwitchToAi}
            onNext={handleApplyManual}
          />
        )}

        {step === 3 && (
          <StepTopics
            key="step-topics"
            selectedTopics={selectedTopics}
            toggleTopic={toggleTopic}
            onBack={() => setStep(2)}
            onNext={() => {
              setProfile((prev) => ({ ...prev, specializations: selectedTopics }));
              setStep(4);
            }}
          />
        )}

        {step === 4 && (
          <StepPhoto
            key="step-photo"
            userId={userId}
            avatarUrl={avatarUrl}
            onAvatarChange={onAvatarChange}
            profileName={profile.name || `${firstName} ${lastName}`.trim()}
            onBack={() => setStep(3)}
            onFinish={handleFinish}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function StepName({ firstName, lastName, setFirstName, setLastName, onNext }) {
  const canProceed = firstName.trim().length > 0;
  return (
    <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-600/20 flex items-center justify-center">
          <User className="w-8 h-8 text-primary-400" />
        </div>
        <h2 className="text-2xl font-medium text-white mb-2">Как вас зовут?</h2>
        <p className="text-lg font-extralight text-neutral-400">Представьтесь, чтобы клиенты знали, с кем работают</p>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-lg font-extralight text-neutral-300 mb-2">Имя</label>
          <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Введите имя"
            className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors" />
        </div>
        <div>
          <label className="block text-lg font-extralight text-neutral-300 mb-2">Фамилия</label>
          <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Введите фамилию"
            className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors" />
        </div>
      </div>
      <button onClick={onNext} disabled={!canProceed}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors">
        Продолжить <ChevronRight className="w-5 h-5" />
      </button>
    </motion.div>
  );
}

function StepChooseMethod({ onBack, onChooseAi, onChooseManual }) {
  return (
    <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-medium text-white mb-2">Заполните информацию о себе</h2>
        <p className="text-lg font-extralight text-neutral-400">Выберите удобный способ заполнения профиля</p>
      </div>
      <div className="grid gap-4">
        <button onClick={onChooseAi}
          className="p-6 rounded-md border border-primary-600/30 bg-primary-950/20 hover:bg-primary-950/40 hover:border-primary-500/50 transition-all text-left group">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-primary-600/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-white mb-1 group-hover:text-primary-300 transition-colors">Заполнить с AI-помощником</h3>
              <p className="text-lg font-extralight text-neutral-400">
                Загрузите резюме, и AI-помощник автоматически извлечёт данные: должность, опыт работы, достижения и специализации
              </p>
            </div>
          </div>
        </button>
        <button onClick={onChooseManual}
          className="p-6 rounded-md border border-neutral-700 bg-neutral-900/30 hover:bg-neutral-800/50 hover:border-neutral-600 transition-all text-left group">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-neutral-700/50 flex items-center justify-center flex-shrink-0">
              <PenLine className="w-6 h-6 text-neutral-300" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-white mb-1 group-hover:text-neutral-200 transition-colors">Заполнить самостоятельно</h3>
              <p className="text-lg font-extralight text-neutral-400">
                Вручную заполните информацию о себе: должность, места работы, достижения и описание
              </p>
            </div>
          </div>
        </button>
      </div>
      <button onClick={onBack} className="flex items-center gap-2 text-neutral-400 hover:text-white text-lg font-extralight transition-colors">
        <ChevronLeft className="w-5 h-5" /> Назад
      </button>
    </motion.div>
  );
}

function StepAiFill({ aiStep, file, setFile, fileInputRef, aiLoading, aiError, setAiError, aiResult, selectedAiFields, setSelectedAiFields, expandedAi, setExpandedAi, onBack, handleFileSelect, handleDrop, handleAnalyze, handleApplyAi, handleReset, countAiFields, profile, onPreview }) {
  if (aiStep === "upload") {
    return (
      <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="space-y-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-600/20 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-primary-400" />
          </div>
          <h2 className="text-2xl font-medium text-white mb-2">AI-помощник</h2>
          <p className="text-lg font-extralight text-neutral-400">Загрузите резюме для автоматического заполнения</p>
        </div>
        {aiError && (
          <div className="flex items-center gap-3 p-4 rounded-md bg-red-500/10 border border-red-500/20">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-lg font-extralight text-red-300">{aiError}</p>
          </div>
        )}
        <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${file ? "border-primary-500/40 bg-primary-500/5" : "border-neutral-700 hover:border-primary-500/30 hover:bg-neutral-800/30"}`}>
          <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.txt" onChange={handleFileSelect} className="hidden" />
          {file ? (
            <div className="flex items-center justify-center gap-4">
              <div className="w-12 h-12 rounded-md bg-primary-500/20 flex items-center justify-center"><FileText className="w-6 h-6 text-primary-400" /></div>
              <div className="text-left">
                <p className="text-lg font-medium text-white">{file.name}</p>
                <p className="text-lg font-extralight text-neutral-400">{getFileTypeLabel(file.type)} / {(file.size / 1024).toFixed(0)} КБ</p>
              </div>
              <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="p-2 rounded-md hover:bg-neutral-700 text-neutral-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 text-neutral-500 mx-auto mb-3" />
              <p className="text-lg font-extralight text-neutral-300 mb-1">Перетащите файл сюда или нажмите для выбора</p>
              <p className="text-lg font-extralight text-neutral-500">PDF, Word (.doc, .docx), TXT / до 5 МБ</p>
            </>
          )}
        </div>
        <div className="flex justify-between gap-3">
          <button onClick={onBack} className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-neutral-700 text-neutral-300 hover:text-white text-lg font-extralight transition-colors">
            <ChevronLeft className="w-4 h-4" /> Назад
          </button>
          <button onClick={handleAnalyze} disabled={!file || aiLoading}
            className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors">
            {aiLoading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Анализ...</>) : (<><Sparkles className="w-5 h-5" /> Проанализировать</>)}
          </button>
        </div>
      </motion.div>
    );
  }

  if (aiStep === "review" && aiResult) {
    const { total, selected } = countAiFields();
    const hasAnyData = total > 0 || (aiResult && typeof aiResult === "object" && Object.keys(aiResult).length > 0);
    return (
      <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="space-y-4">
        <div className="flex items-start gap-3 p-4 rounded-md bg-accent-500/10 border border-accent-500/20">
          <Eye className="w-5 h-5 text-accent-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-lg font-medium text-accent-300 mb-1">Результат анализа</p>
            <p className="text-lg font-extralight text-accent-300/80">Просмотрите данные, снимите галочку с ненужных полей и нажмите "Принять".</p>
          </div>
        </div>
        {total > 0 && (
          <div className="flex items-center justify-between px-1">
            <p className="text-lg font-extralight text-neutral-400">Выбрано: {selected} из {total}</p>
            <button onClick={() => {
              const allOn = Object.values(selectedAiFields).every(Boolean);
              const s = {};
              Object.keys(selectedAiFields).forEach((k) => { s[k] = !allOn; });
              setSelectedAiFields(s);
            }} className="text-lg font-extralight text-primary-400 hover:text-primary-300 transition-colors">
              {Object.values(selectedAiFields).every(Boolean) ? "Снять все" : "Выбрать все"}
            </button>
          </div>
        )}
        {total === 0 && hasAnyData && (
          <div className="flex items-start gap-3 p-4 rounded-md bg-amber-500/10 border border-amber-500/20">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-lg font-medium text-amber-300 mb-1">Данные извлечены частично</p>
              <p className="text-lg font-extralight text-amber-300/80">AI извлёк данные, но они не совпали со стандартными полями. Нажмите "Просмотреть", чтобы увидеть и отредактировать результат вручную.</p>
            </div>
          </div>
        )}

        {aiResult.title && <AiField label="Должность" value={aiResult.title} selected={selectedAiFields.title} onToggle={() => setSelectedAiFields((p) => ({ ...p, title: !p.title }))} expanded={expandedAi.title} onExpand={() => setExpandedAi((p) => ({ ...p, title: !p.title }))} current={profile.title} />}
        {aiResult.bio && <AiField label="О себе" value={aiResult.bio} selected={selectedAiFields.bio} onToggle={() => setSelectedAiFields((p) => ({ ...p, bio: !p.bio }))} expanded={expandedAi.bio} onExpand={() => setExpandedAi((p) => ({ ...p, bio: !p.bio }))} current={profile.bio} multiline />}
        {aiResult.companies?.length > 0 && <AiList label="Места работы" items={aiResult.companies.map((c) => `${c.name} — ${c.position} (${c.years})`)} selected={selectedAiFields.companies} onToggle={() => setSelectedAiFields((p) => ({ ...p, companies: !p.companies }))} expanded={expandedAi.companies} onExpand={() => setExpandedAi((p) => ({ ...p, companies: !p.companies }))} />}
        {aiResult.achievements?.length > 0 && <AiList label="Достижения" items={aiResult.achievements} selected={selectedAiFields.achievements} onToggle={() => setSelectedAiFields((p) => ({ ...p, achievements: !p.achievements }))} expanded={expandedAi.achievements} onExpand={() => setExpandedAi((p) => ({ ...p, achievements: !p.achievements }))} />}
        {aiResult.specializations?.length > 0 && <AiList label="Специализации" items={aiResult.specializations} selected={selectedAiFields.specializations} onToggle={() => setSelectedAiFields((p) => ({ ...p, specializations: !p.specializations }))} expanded={expandedAi.specializations} onExpand={() => setExpandedAi((p) => ({ ...p, specializations: !p.specializations }))} />}

        <div className="flex justify-between gap-3 pt-4">
          <button onClick={handleReset} className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-neutral-700 text-neutral-300 hover:text-white text-lg font-extralight transition-colors">
            <RotateCcw className="w-4 h-4" /> Другой файл
          </button>
          <div className="flex items-center gap-3">
            <button onClick={onPreview} disabled={!hasAnyData}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-primary-500/30 text-primary-400 hover:bg-primary-500/10 disabled:border-neutral-700 disabled:text-neutral-600 disabled:cursor-not-allowed text-lg font-extralight transition-colors">
              <Eye className="w-4 h-4" /> Просмотреть
            </button>
            <button onClick={handleApplyAi} disabled={!hasAnyData && selected === 0}
              className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-accent-600 hover:bg-accent-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors">
              <CheckCircle className="w-5 h-5" /> Принять и продолжить
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return null;
}

function StepManualFill({ manualTitle, setManualTitle, manualBio, setManualBio, manualCompanies, setManualCompanies, manualAchievements, setManualAchievements, onBack, onSwitchToAi, onNext, isAiPreview }) {
  const addCompany = () => setManualCompanies((p) => [...p, { name: "", position: "", years: "" }]);
  const removeCompany = (i) => setManualCompanies((p) => p.filter((_, idx) => idx !== i));
  const updateCompany = (i, field, value) => setManualCompanies((p) => p.map((c, idx) => idx === i ? { ...c, [field]: value } : c));
  const addAchievement = () => setManualAchievements((p) => [...p, ""]);
  const removeAchievement = (i) => setManualAchievements((p) => p.filter((_, idx) => idx !== i));
  const updateAchievement = (i, value) => setManualAchievements((p) => p.map((a, idx) => idx === i ? value : a));

  return (
    <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="space-y-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-medium text-white">{isAiPreview ? "Предпросмотр данных AI" : "Заполнение профиля"}</h2>
        {onSwitchToAi && (
          <button onClick={onSwitchToAi} className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-primary-600/30 text-primary-400 hover:bg-primary-500/10 text-lg font-extralight transition-colors">
            <Sparkles className="w-4 h-4" /> Заполнить с AI
          </button>
        )}
        {isAiPreview && (
          <span className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary-500/10 text-primary-400 text-lg font-extralight">
            <Sparkles className="w-4 h-4" /> Данные от AI
          </span>
        )}
      </div>
      <div>
        <label className="block text-lg font-extralight text-neutral-300 mb-2">Должность / роль</label>
        <input type="text" value={manualTitle} onChange={(e) => setManualTitle(e.target.value)} placeholder="Chief Marketing Officer"
          className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 transition-colors" />
      </div>
      <div>
        <label className="block text-lg font-extralight text-neutral-300 mb-2">О себе</label>
        <textarea value={manualBio} onChange={(e) => setManualBio(e.target.value)} rows={4} placeholder="Расскажите о своём опыте и экспертизе..."
          className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-500 focus:outline-none focus:border-primary-500 resize-none transition-colors" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-lg font-extralight text-neutral-300 flex items-center gap-2"><Building2 className="w-4 h-4 text-primary-400" /> Места работы</label>
          <button onClick={addCompany} className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 text-lg font-extralight transition-colors"><Plus className="w-4 h-4" /> Добавить</button>
        </div>
        <div className="space-y-3">
          {manualCompanies.map((c, i) => (
            <div key={i} className="flex gap-2 items-start">
              <div className="flex-1 grid sm:grid-cols-3 gap-2">
                <input type="text" value={c.name} onChange={(e) => updateCompany(i, "name", e.target.value)} placeholder="Компания"
                  className="px-3 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500" />
                <input type="text" value={c.position} onChange={(e) => updateCompany(i, "position", e.target.value)} placeholder="Должность"
                  className="px-3 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500" />
                <input type="text" value={c.years} onChange={(e) => updateCompany(i, "years", e.target.value)} placeholder="Годы"
                  className="px-3 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500" />
              </div>
              {manualCompanies.length > 1 && (
                <button onClick={() => removeCompany(i)} className="p-2 rounded-md hover:bg-red-500/10 text-neutral-500 hover:text-red-400 transition-colors mt-0.5"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-lg font-extralight text-neutral-300 flex items-center gap-2"><Award className="w-4 h-4 text-accent-400" /> Достижения</label>
          <button onClick={addAchievement} className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 text-lg font-extralight transition-colors"><Plus className="w-4 h-4" /> Добавить</button>
        </div>
        <div className="space-y-2">
          {manualAchievements.map((a, i) => (
            <div key={i} className="flex gap-2">
              <input type="text" value={a} onChange={(e) => updateAchievement(i, e.target.value)} placeholder="Описание достижения"
                className="flex-1 px-3 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500" />
              {manualAchievements.length > 1 && (
                <button onClick={() => removeAchievement(i)} className="p-2 rounded-md hover:bg-red-500/10 text-neutral-500 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-between gap-3 pt-2">
        <button onClick={onBack} className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-neutral-700 text-neutral-300 hover:text-white text-lg font-extralight transition-colors">
          <ChevronLeft className="w-4 h-4" /> {isAiPreview ? "Назад к результатам" : "Назад"}
        </button>
        <button onClick={onNext} className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-medium transition-colors">
          {isAiPreview ? "Принять и продолжить" : "Продолжить"} <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
}

function StepTopics({ selectedTopics, toggleTopic, onBack, onNext }) {
  return (
    <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-600/20 flex items-center justify-center"><Tag className="w-8 h-8 text-accent-400" /></div>
        <h2 className="text-2xl font-medium text-white mb-2">Тематики консультаций</h2>
        <p className="text-lg font-extralight text-neutral-400">Выберите до {MAX_TOPICS} тематик, по которым вы готовы консультировать</p>
      </div>
      <div className="flex items-center justify-between px-1">
        <p className={`text-lg font-extralight ${selectedTopics.length >= MAX_TOPICS ? "text-amber-400" : "text-neutral-400"}`}>
          Выбрано: {selectedTopics.length} из {MAX_TOPICS}
          {selectedTopics.length >= MAX_TOPICS && " (максимум)"}
        </p>
        {selectedTopics.length > 0 && (
          <button onClick={() => { selectedTopics.forEach((t) => toggleTopic(t)); }} className="text-lg font-extralight text-neutral-500 hover:text-neutral-300 transition-colors">Сбросить</button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {allSpecializations.map((spec) => {
          const isSelected = selectedTopics.includes(spec);
          const isDisabled = !isSelected && selectedTopics.length >= MAX_TOPICS;
          return (
            <button key={spec} type="button" onClick={() => !isDisabled && toggleTopic(spec)} disabled={isDisabled}
              className={`px-4 py-2 rounded-md text-lg font-extralight transition-all ${
                isSelected ? "bg-primary-600/20 text-primary-300 border border-primary-500/40"
                : isDisabled ? "bg-neutral-800/20 text-neutral-600 border border-neutral-800/30 cursor-not-allowed"
                : "bg-neutral-800/50 text-neutral-400 border border-neutral-700/50 hover:border-neutral-600 hover:text-neutral-300"
              }`}>
              {spec}
            </button>
          );
        })}
      </div>
      {selectedTopics.length > 0 && (
        <div className="pt-2">
          <div className="flex flex-wrap gap-2">
            {selectedTopics.map((spec) => (
              <span key={spec} className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary-500/10 text-primary-400 text-lg font-extralight">
                {spec}
                <button onClick={() => toggleTopic(spec)} className="hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-between gap-3 pt-4">
        <button onClick={onBack} className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-neutral-700 text-neutral-300 hover:text-white text-lg font-extralight transition-colors">
          <ChevronLeft className="w-4 h-4" /> Назад
        </button>
        <button onClick={onNext} disabled={selectedTopics.length === 0}
          className="flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors">
          Продолжить <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
}

function AiField({ label, value, selected, onToggle, expanded, onExpand, current, multiline }) {
  if (!value) return null;
  const hasExisting = current && current.length > 0;
  const hasChanged = hasExisting && current !== value;
  return (
    <div className={`rounded-md border transition-colors ${selected ? "border-primary-500/30 bg-primary-500/5" : "border-neutral-700/50 bg-neutral-800/20 opacity-60"}`}>
      <div className="flex items-center justify-between p-3 cursor-pointer" onClick={onExpand}>
        <div className="flex items-center gap-3">
          <button onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className={`w-5 h-5 rounded flex items-center justify-center border transition-colors flex-shrink-0 ${selected ? "bg-primary-600 border-primary-600" : "border-neutral-600"}`}>
            {selected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
          </button>
          <span className="text-lg font-extralight text-neutral-300">{label}</span>
          {hasChanged && <span className="text-lg font-extralight text-amber-400/80">(изменение)</span>}
          {!hasExisting && <span className="text-lg font-extralight text-green-400/80">(новое)</span>}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-3 pb-3 pt-0">
              <div className="p-3 rounded bg-primary-500/5 border border-primary-500/10">
                <p className="text-lg font-extralight text-primary-400 mb-1">Значение от AI:</p>
                <p className={`text-lg font-extralight text-white ${multiline ? "leading-relaxed whitespace-pre-line" : "font-medium"}`}>{value}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function AiList({ label, items, selected, onToggle, expanded, onExpand }) {
  if (!items || items.length === 0) return null;
  return (
    <div className={`rounded-md border transition-colors ${selected ? "border-primary-500/30 bg-primary-500/5" : "border-neutral-700/50 bg-neutral-800/20 opacity-60"}`}>
      <div className="flex items-center justify-between p-3 cursor-pointer" onClick={onExpand}>
        <div className="flex items-center gap-3">
          <button onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className={`w-5 h-5 rounded flex items-center justify-center border transition-colors flex-shrink-0 ${selected ? "bg-primary-600 border-primary-600" : "border-neutral-600"}`}>
            {selected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
          </button>
          <span className="text-lg font-extralight text-neutral-300">{label}</span>
          <span className="text-lg font-extralight text-neutral-500">({items.length})</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-3 pb-3 pt-0">
              <div className="p-3 rounded bg-primary-500/5 border border-primary-500/10">
                <ul className="space-y-1.5">
                  {items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1 flex-shrink-0">-</span>
                      <span className="text-lg font-extralight text-white">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}